import { useState } from 'react';
import { formatDateTime } from '../lib/format.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
  Switch,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SensorsIcon from '@mui/icons-material/Sensors';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { SsoCard } from './SsoCard.js';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';

interface Overview {
  deployment: { singleTenant: boolean; orgName: string; environment: string; version: string; storageDriver: string; storageLocation: string; queueDriver: string; searchEnabled: boolean };
  members: { total: number; owners: number; admins: number; byRole: Record<string, number> };
  apiKeys: { active: number };
}
interface PiiPolicy {
  enabled: boolean;
  entities: string[];
  failOpen: boolean;
}
interface PiiMaskingDto {
  policy: PiiPolicy;
  selectableEntities: string[];
  analyzerHealthy: boolean;
  /** False on plans below Scale — the toggle is shown but locked. */
  entitled: boolean;
}

/** One click for the access reviews auditors ask for. */
const AUDIT_PRESETS = ['auth.login', 'auth.login.failed', 'session.replay.view'] as const;

interface AuditEntry {
  id: string;
  action: string;
  targetType: string;
  targetId?: string;
  actor: { id: string; name?: string; email: string } | null;
  metadata?: Record<string, unknown>;
  ip?: string;
  userAgent?: string;
  createdAt: string;
}

export function SecurityPage() {
  const t = useT().security;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const base = `/v1/orgs/${orgId}/security`;
  const [action, setAction] = useState('');

  const overview = useQuery({
    enabled: !!orgId,
    queryKey: ['security-overview', orgId],
    queryFn: async () => (await api.get(`${base}/overview`)).data as Overview,
  });

  const audit = useQuery({
    enabled: !!orgId,
    queryKey: ['security-audit', orgId, action],
    queryFn: async () => {
      const p = new URLSearchParams({ limit: '200' });
      if (action) p.set('action', action);
      return (await api.get(`${base}/audit?${p}`)).data.data as AuditEntry[];
    },
  });

  const qc = useQueryClient();
  const masking = useQuery({
    enabled: !!orgId,
    queryKey: ['security-masking', orgId],
    queryFn: async () => (await api.get(`${base}/masking`)).data as { maskInputs: boolean },
  });
  const setMasking = useMutation({
    mutationFn: async (maskInputs: boolean) =>
      (await api.put(`${base}/masking`, { maskInputs })).data as { maskInputs: boolean },
    onSuccess: (data) => {
      qc.setQueryData(['security-masking', orgId], data);
      qc.invalidateQueries({ queryKey: ['security-audit', orgId] });
    },
  });
  const maskInputs = masking.data?.maskInputs ?? true;

  // PII masking for outbound content (AI prompts, tracker issues).
  const piiMasking = useQuery({
    enabled: !!orgId,
    queryKey: ['security-pii-masking', orgId],
    queryFn: async () => (await api.get(`${base}/pii-masking`)).data as PiiMaskingDto,
  });
  const [piiDraft, setPiiDraft] = useState<PiiPolicy | null>(null);
  const savePii = useMutation({
    mutationFn: async (policy: PiiPolicy) => (await api.put(`${base}/pii-masking`, policy)).data as PiiPolicy,
    onSuccess: (policy) => {
      qc.setQueryData(['security-pii-masking', orgId], (prev: PiiMaskingDto | undefined) =>
        prev ? { ...prev, policy } : prev,
      );
      setPiiDraft(null);
      qc.invalidateQueries({ queryKey: ['security-audit', orgId] });
    },
  });
  const pii = piiDraft ?? piiMasking.data?.policy ?? null;
  const setPii = (patch: Partial<PiiPolicy>) => {
    if (pii) setPiiDraft({ ...pii, ...patch });
  };

  // Enterprise: ingest-time masking — recorded content scrubbed before storage.
  const ingestMasking = useQuery({
    enabled: !!orgId,
    queryKey: ['security-ingest-masking', orgId],
    queryFn: async () => (await api.get(`${base}/ingest-masking`)).data as { enabled: boolean; entitled: boolean },
  });
  const setIngestMasking = useMutation({
    mutationFn: async (enabled: boolean) =>
      (await api.put(`${base}/ingest-masking`, { enabled })).data as { enabled: boolean; entitled: boolean },
    onSuccess: (data) => {
      qc.setQueryData(['security-ingest-masking', orgId], data);
      qc.invalidateQueries({ queryKey: ['security-audit', orgId] });
    },
  });

  // Visitor-facing SDK widgets — served to the SDK through the collector, so
  // flipping these needs no change to the customer's own init() call.
  const liveWidgets = useQuery({
    enabled: !!orgId,
    queryKey: ['security-live-widgets', orgId],
    queryFn: async () => (await api.get(`${base}/live-widgets`)).data as { liveChat: boolean; testerTools: boolean; anonymousFindings: boolean; live: boolean },
  });
  const setLiveWidgets = useMutation({
    mutationFn: async (patch: { liveChat?: boolean; testerTools?: boolean; anonymousFindings?: boolean; live?: boolean }) =>
      (await api.put(`${base}/live-widgets`, patch)).data as { liveChat: boolean; testerTools: boolean; anonymousFindings: boolean; live: boolean },
    onSuccess: (data) => {
      qc.setQueryData(['security-live-widgets', orgId], data);
      qc.invalidateQueries({ queryKey: ['security-audit', orgId] });
    },
  });
  const widgets = liveWidgets.data;

  const o = overview.data;
  const sort = useSort<'when' | 'action' | 'target' | 'actor' | 'ip'>('when');
  const rows = sort.sorted(audit.data ?? [], {
    when: (a) => a.createdAt,
    action: (a) => a.action,
    target: (a) => `${a.targetType} ${a.targetId ?? ''}`,
    // The directory acts with no actor at all; those sink rather than lead.
    actor: (a) => a.actor?.email,
    ip: (a) => a.ip,
  });

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <ShieldIcon color="primary" />
        <Typography variant="h5">{t.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t.subtitle}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>{t.deployment.heading}</Typography>
          <Row label={t.deployment.mode}>
            <Chip
              size="small"
              label={o?.deployment.singleTenant ? t.deployment.singleTenant : t.deployment.multiTenant}
              color={o?.deployment.singleTenant ? 'primary' : 'default'}
            />
          </Row>
          <Row label={t.deployment.organization}>{o?.deployment.orgName ?? '—'}</Row>
          <Row label={t.deployment.environment}>
            <Chip size="small" variant="outlined" label={o?.deployment.environment ?? '—'} />
          </Row>
          <Row label={t.deployment.version}>v{o?.deployment.version ?? '—'}</Row>
          <Row label={t.deployment.storage}>
            <Chip
              size="small"
              label={o?.deployment.storageDriver === 'local' ? t.deployment.localDisk : 'S3'}
              color={o?.deployment.storageDriver === 'local' ? 'default' : 'primary'}
            />
          </Row>
          {o?.deployment.storageLocation && (
            <Row label={o.deployment.storageDriver === 'local' ? t.deployment.path : t.deployment.bucket}>
              <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>
                {o.deployment.storageLocation}
              </Typography>
            </Row>
          )}
          <Row label={t.deployment.queue}>
            <Chip size="small" label={o?.deployment.queueDriver === 'redis' ? 'Redis' : 'SQS'} color={o?.deployment.queueDriver === 'redis' ? 'default' : 'primary'} />
          </Row>
          <Row label={t.deployment.search}>
            <Chip
              size="small"
              label={o?.deployment.searchEnabled ? 'OpenSearch' : t.deployment.searchOff}
              color={o?.deployment.searchEnabled ? 'primary' : 'default'}
            />
          </Row>
        </Paper>

        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>{t.people.heading}</Typography>
          <Row label={t.people.members}>{o?.members.total ?? 0}</Row>
          <Row label={t.people.owners}>{o?.members.owners ?? 0}</Row>
          <Row label={t.people.admins}>{o?.members.admins ?? 0}</Row>
          <Row label={t.people.activeKeys}>{o?.apiKeys.active ?? 0}</Row>
        </Paper>

        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>{t.people.roles}</Typography>
          {o
            ? Object.entries(o.members.byRole).map(([role, n]) => (
                <Row key={role} label={t.roleNames[role] ?? role}>{n}</Row>
              ))
            : <Typography variant="body2" color="text.disabled">—</Typography>}
        </Paper>
      </Box>

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <VisibilityOffIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={700}>{t.privacy.heading}</Typography>
        </Stack>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ maxWidth: 620 }}>
            <Typography variant="body2" fontWeight={600}>{t.privacy.maskInputs}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t.privacy.maskInputsBody}
            </Typography>
            {!maskInputs && (
              <Chip
                size="small"
                color="warning"
                label={t.privacy.maskInputsWarning}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
          <Switch
            checked={maskInputs}
            disabled={masking.isLoading || setMasking.isPending}
            onChange={(e) => setMasking.mutate(e.target.checked)}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ maxWidth: 620 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={600}>{t.privacy.pii}</Typography>
              <Chip size="small" color="secondary" variant="outlined" label={t.privacy.scale} />
              {piiMasking.data && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={piiMasking.data.analyzerHealthy ? 'success' : 'default'}
                  label={piiMasking.data.analyzerHealthy ? t.privacy.detectorOnline : t.privacy.detectorOffline}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t.privacy.piiBody}
            </Typography>
            {piiMasking.data && !piiMasking.data.entitled && (
              <Chip size="small" color="default" label={t.privacy.piiUpgrade} sx={{ mt: 1 }} />
            )}
            {pii?.enabled && (
              <Box sx={{ mt: 1.5 }}>
                {(piiMasking.data?.selectableEntities ?? []).map((e) => (
                  <FormControlLabel
                    key={e}
                    sx={{ mr: 2 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={pii.entities.includes(e)}
                        onChange={(ev) =>
                          setPii({
                            entities: ev.target.checked
                              ? [...pii.entities, e]
                              : pii.entities.filter((x) => x !== e),
                          })
                        }
                      />
                    }
                    label={<Typography variant="body2">{t.entities[e] ?? e}</Typography>}
                  />
                ))}
                <FormControlLabel
                  sx={{ display: 'block', mt: 1 }}
                  control={
                    <Switch
                      size="small"
                      checked={!pii.failOpen}
                      onChange={(ev) => setPii({ failOpen: !ev.target.checked })}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t.privacy.failClosed}
                    </Typography>
                  }
                />
              </Box>
            )}
            {piiDraft && (
              <Button
                size="small"
                variant="contained"
                sx={{ mt: 1 }}
                disabled={savePii.isPending}
                onClick={() => savePii.mutate(piiDraft)}
              >
                {t.privacy.savePolicy}
              </Button>
            )}
          </Box>
          <Tooltip title={piiMasking.data && !piiMasking.data.entitled ? t.privacy.piiGate : ''}>
            <span>
              <Switch
                checked={pii?.enabled ?? false}
                disabled={!pii || savePii.isPending || !piiMasking.data?.entitled}
                onChange={(e) => {
                  const next = { ...(pii as PiiPolicy), enabled: e.target.checked };
                  setPiiDraft(next);
                  savePii.mutate(next);
                }}
              />
            </span>
          </Tooltip>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ maxWidth: 620 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={600}>{t.privacy.ingest}</Typography>
              <Chip size="small" color="secondary" variant="outlined" label={t.privacy.enterprise} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t.privacy.ingestBody}
            </Typography>
            {ingestMasking.data && !ingestMasking.data.entitled && (
              <Chip size="small" color="default" label={t.privacy.ingestUpgrade} sx={{ mt: 1 }} />
            )}
          </Box>
          <Tooltip
            title={
              ingestMasking.data && !ingestMasking.data.entitled
                ? t.privacy.ingestGate
                : ''
            }
          >
            <span>
              <Switch
                checked={ingestMasking.data?.enabled ?? false}
                disabled={
                  ingestMasking.isLoading || setIngestMasking.isPending || !ingestMasking.data?.entitled
                }
                onChange={(e) => setIngestMasking.mutate(e.target.checked)}
              />
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <SensorsIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={700}>{t.widgets.heading}</Typography>
        </Stack>

        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ maxWidth: 620 }}>
            <Typography variant="body2" fontWeight={600}>{t.widgets.chat}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t.widgets.chatBody}
            </Typography>
          </Box>
          <Tooltip title={widgets && !widgets.live ? t.widgets.chatDisabledHint : ''}>
            <span>
              <Switch
                checked={(widgets?.liveChat ?? true) && (widgets?.live ?? true)}
                disabled={liveWidgets.isLoading || setLiveWidgets.isPending || !widgets?.live}
                onChange={(e) => setLiveWidgets.mutate({ liveChat: e.target.checked })}
              />
            </span>
          </Tooltip>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ maxWidth: 620 }}>
            <Typography variant="body2" fontWeight={600}>{t.widgets.tester}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t.widgets.testerBody}
            </Typography>
          </Box>
          <Tooltip title={widgets && !widgets.live ? t.widgets.chatDisabledHint : ''}>
            <span>
              <Switch
                checked={(widgets?.testerTools ?? true) && (widgets?.live ?? true)}
                disabled={liveWidgets.isLoading || setLiveWidgets.isPending || !widgets?.live}
                onChange={(e) => setLiveWidgets.mutate({ testerTools: e.target.checked })}
              />
            </span>
          </Tooltip>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ maxWidth: 620 }}>
            <Typography variant="body2" fontWeight={600}>{t.widgets.anonymous}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t.widgets.anonymousBody}
            </Typography>
          </Box>
          <Tooltip title={widgets && !widgets.testerTools ? t.widgets.anonymousNeedsTester : ''}>
            <span>
              <Switch
                checked={widgets?.anonymousFindings ?? false}
                disabled={liveWidgets.isLoading || setLiveWidgets.isPending || !widgets?.testerTools || !widgets?.live}
                onChange={(e) => setLiveWidgets.mutate({ anonymousFindings: e.target.checked })}
              />
            </span>
          </Tooltip>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ maxWidth: 620 }}>
            <Typography variant="body2" fontWeight={600}>{t.widgets.live}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t.widgets.liveBody}
            </Typography>
          </Box>
          <Switch
            checked={widgets?.live ?? true}
            disabled={liveWidgets.isLoading || setLiveWidgets.isPending}
            onChange={(e) => setLiveWidgets.mutate({ live: e.target.checked })}
          />
        </Stack>
      </Paper>

      <SsoCard orgId={orgId ?? ''} />

      <Paper sx={{ overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2, pb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={700}>{t.audit.heading}</Typography>
            {/* The reviews people actually run: "every sign-in last quarter",
                "who opened a recording". Typing the action prefixes by hand is
                the step that made the trail feel unusable. */}
            {AUDIT_PRESETS.map((p) => (
              <Chip
                key={p}
                size="small"
                label={t.audit.presets[p] ?? p}
                variant={action === p ? 'filled' : 'outlined'}
                color={action === p ? 'primary' : 'default'}
                onClick={() => setAction(action === p ? '' : p)}
              />
            ))}
          </Stack>
          <TextField
            size="small"
            placeholder={t.audit.filterPlaceholder}
            value={action}
            onChange={(e) => setAction(e.target.value)}
            sx={{ width: 260 }}
          />
        </Stack>
        {audit.isLoading && <LinearProgress />}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t.audit.columns.when}</TableCell>
                <TableCell>{t.audit.columns.action}</TableCell>
                <TableCell>{t.audit.columns.target}</TableCell>
                <TableCell>{t.audit.columns.actor}</TableCell>
                <TableCell>{t.audit.columns.ip}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(e.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={e.action} sx={{ bgcolor: `${actionColor(e.action)}22`, color: actionColor(e.action), fontWeight: 600, fontFamily: 'ui-monospace, monospace' }} />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={e.targetId ?? ''}>
                      <Typography variant="body2">{e.targetType}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{e.actor ? e.actor.name || e.actor.email : t.audit.system}</Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={e.userAgent ?? ''}>
                      <Typography variant="caption" color="text.secondary">{e.ip || '—'}</Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!audit.isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      {action ? t.audit.emptyFiltered : t.audit.empty}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ py: 0.6 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Box sx={{ textAlign: 'right' }}>
        {typeof children === 'string' || typeof children === 'number' ? (
          <Typography variant="body2" fontWeight={600}>{children}</Typography>
        ) : (
          children
        )}
      </Box>
    </Stack>
  );
}

function actionColor(action: string): string {
  // Destructive first — "deactivate" contains "activate", so order matters.
  if (/delete|remove|revoke|deactivate|erase|disable/i.test(action)) return '#ef4444';
  if (/create|activate|upsert|invite|accept/i.test(action)) return '#2ec2b3';
  if (/update|set|rotate|change/i.test(action)) return '#3b82f6';
  return '#64748b';
}
